import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader, Flex, Heading, HStack, Link, Select, Spinner,
  Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { ChatWidgetAPI } from '../../services/api';
import { formatCallTime, useCallTimezone } from '../../utils/callTimezone';
import CorrectionPanel from './corrections/CorrectionPanel';
import ChatHandoffPanel, { handoffStateBadge } from './ChatHandoffPanel';
import ChatAttachment from '../../components/chat/ChatAttachment';
import { maskPhone } from './serviceRecords';
import { ticketReturnPath, validTicketNumber } from './chatNavigation';

const PAGE_SIZE = 25;

// Contact badges for a Chatbox visitor: phone > email > order number.
export const chatContactBadges = (contact = {}, lang = 'zh') => {
  const zh = lang === 'zh';
  const badges = [];
  (contact.phones || []).slice(0, 1).forEach((p) => badges.push({ kind: 'phone', label: `${zh ? '电话' : 'Phone'} ${maskPhone(p)}` }));
  (contact.emails || []).slice(0, 1).forEach((e) => badges.push({ kind: 'email', label: `${zh ? '邮箱' : 'Email'} ${e.replace(/^(.).*(@.*)$/, '$1***$2')}` }));
  (contact.order_numbers || []).slice(0, 2).forEach((o) => badges.push({ kind: 'order', label: `${zh ? '订单' : 'Order'} ${o}` }));
  return badges;
};

const COLORS = { phone: 'blue', email: 'purple', order: 'orange' };

const ALERT_REASONS = {
  no_recipient: ['没有收件邮箱', 'no recipient address'],
  send_failed: ['发送失败', 'sending failed'],
  exception: ['发送出错', 'sending error'],
};

// How the escalation email to the merchant went (metadata.escalation.alert); null before it ran.
export const escalationAlertBadge = (alert, lang = 'zh') => {
  if (!alert || typeof alert !== 'object') return null;
  const zh = lang === 'zh';
  const sent = Number(alert.sent) || 0;
  const recipients = Number(alert.recipients) || 0;
  if (sent > 0) {
    const count = recipients > sent ? `${sent}/${recipients}` : `${sent}`;
    return { color: alert.error ? 'orange' : 'green', label: zh ? `邮件已通知（${count}）` : `Email sent (${count})` };
  }
  const reason = ALERT_REASONS[alert.error] ? ALERT_REASONS[alert.error][zh ? 0 : 1] : (alert.error || (zh ? '未知原因' : 'unknown reason'));
  return { color: 'red', label: zh ? `邮件未发送：${reason}` : `Email not sent: ${reason}` };
};

const GUARD_REASONS = {
  link: ['移除了外部链接', 'removed an outside link'],
  leak: ['疑似泄露内部规则或知识库', 'looked like leaking instructions or the knowledge base'],
  commitment: ['未经确认的价格/折扣/退款/时效承诺', 'unconfirmed price, discount, refund or delivery promise'],
  format: ['清理了图片/HTML', 'removed images or HTML'],
  length: ['回复过长已截断', 'reply too long, shortened'],
  guard_error: ['检查出错，已移除链接', 'check failed, links removed'],
};

// The chat output guard rewrote this AI reply (metadata.guard: [{rule, action}]); null when untouched.
export const guardBadge = (guard, lang = 'zh') => {
  if (!Array.isArray(guard) || !guard.length) return null;
  const zh = lang === 'zh';
  const reasons = [...new Set(guard.map((g) => (GUARD_REASONS[g && g.rule] ? GUARD_REASONS[g.rule][zh ? 0 : 1] : String((g && g.rule) || ''))))]
    .filter(Boolean);
  const why = reasons.join(zh ? '、' : '; ');
  return { color: 'orange', label: zh ? `已自动改写（${why}）` : `Automatically rewritten (${why})` };
};

// List query for a filter: '' all, escalated / open (ticket or not), awaiting (待回复: a visitor
// waits for a person, W-H).
export const chatListParams = (filter, page = 1) => ({
  page, page_size: PAGE_SIZE,
  escalated: filter === 'escalated' ? true : filter === 'open' ? false : undefined,
  awaiting: filter === 'awaiting' ? true : undefined,
});

export function ChatRecordList({ portalBasePath, lang = 'zh' }) {
  const zh = lang === 'zh';
  const [displayTimezone] = useCallTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const filter = ['escalated', 'open', 'awaiting'].includes(searchParams.get('chat_filter')) ? searchParams.get('chat_filter') : '';
  const requestedPage = Number(searchParams.get('chat_page'));
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const changeList = (nextFilter, nextPage) => {
    const params = new URLSearchParams(searchParams);
    if (nextFilter) params.set('chat_filter', nextFilter); else params.delete('chat_filter');
    if (nextPage > 1) params.set('chat_page', String(nextPage)); else params.delete('chat_page');
    setSearchParams(params, { replace: true });
  };
  const openConversation = (event, id) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const main = event.currentTarget.closest('.admin-main');
    navigate(`${portalBasePath}/call-history/chat/${encodeURIComponent(id)}`, { state: { chatListSearch: location.search, chatListScroll: main?.scrollTop || 0 } });
  };
  const restoredScroll = useRef(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      setData(await ChatWidgetAPI.chats(chatListParams(filter, page)));
    } catch {
      setError(true);
    }
  }, [page, filter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data || restoredScroll.current || !location.state?.chatListScroll) return;
    const frame = requestAnimationFrame(() => {
      document.querySelector('.admin-main')?.scrollTo?.(0, location.state.chatListScroll);
      restoredScroll.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [data, location.state]);

  const total = data?.pagination?.total || 0;
  return (
    <Box>
      <HStack mb={3}>
        <Select size="sm" maxW="200px" value={filter} onChange={(e) => changeList(e.target.value, 1)} aria-label={zh ? '会话状态' : 'Conversation status'}>
          <option value="">{zh ? '全部对话' : 'All conversations'}</option>
          <option value="escalated">{zh ? '触发工单' : 'Escalated'}</option>
          <option value="open">{zh ? '无工单' : 'Not escalated'}</option>
          <option value="awaiting">{zh ? '待回复' : 'Awaiting reply'}</option>
        </Select>
      </HStack>
      {error && <Alert status="error" mb={3}><AlertIcon />{zh ? '加载失败' : 'Could not load'}</Alert>}
      <Box display={{ base: 'block', md: 'none' }} className="chat-record-mobile">
        {!data && !error && <Spinner label={zh ? '正在加载对话…' : 'Loading conversations…'} />}
        {data && !data.items.length && <Text p={4}>{zh ? '还没有网页客服对话。' : 'No website chats yet.'}</Text>}
        {(data?.items || []).map((conversation) => <Card key={conversation.conversation_id} mb={3}><CardBody>
          <Flex justify="space-between" gap={3} align="start"><Text fontWeight="semibold" noOfLines={2} overflowWrap="anywhere">{conversation.first_message || (zh ? '网页客服对话' : 'Website chat')}</Text>{['requested', 'active'].includes(conversation.handoff_state) && <Badge colorScheme={handoffStateBadge(conversation.handoff_state, lang).color} whiteSpace="normal">{handoffStateBadge(conversation.handoff_state, lang).label}</Badge>}</Flex>
          <Text fontSize="xs" color="text.secondary" mt={2}>{formatCallTime(conversation.last_message_at || conversation.created_at, displayTimezone)}</Text>
          <HStack flexWrap="wrap" mt={2}>{chatContactBadges(conversation.contact, lang).map((badge) => <Badge key={badge.label} colorScheme={COLORS[badge.kind]} textTransform="none">{badge.label}</Badge>)}</HStack>
          {conversation.escalated_at && <Badge mt={2} colorScheme="orange">{zh ? '触发工单' : 'Escalated'}</Badge>}
          <Button as={RouterLink} mt={3} minH="44px" w="100%" to={`${portalBasePath}/call-history/chat/${encodeURIComponent(conversation.conversation_id)}`} onClick={(event) => openConversation(event, conversation.conversation_id)}>{zh ? '查看对话' : 'Open conversation'}</Button>
        </CardBody></Card>)}
      </Box>
      <Card><CardBody>
        <TableContainer display={{ base: 'none', md: 'block' }}>
          <Table size="sm">
            <Thead><Tr><Th>{zh ? '时间' : 'When'}</Th><Th>{zh ? '访客问题' : 'Visitor asked'}</Th><Th>{zh ? '可联系信息' : 'Contact details'}</Th><Th>{zh ? '工单' : 'Ticket'}</Th><Th>{zh ? '消息' : 'Messages'}</Th><Th /></Tr></Thead>
            <Tbody>
              {!data && !error && <Tr><Td colSpan={6} textAlign="center"><Spinner size="sm" /></Td></Tr>}
              {data && !data.items.length && <Tr><Td colSpan={6} textAlign="center" color="gray.500">{zh ? '还没有网页客服对话。' : 'No website chats yet.'}</Td></Tr>}
              {(data?.items || []).map((c) => {
                const badges = chatContactBadges(c.contact, lang);
                return (
                  <Tr key={c.conversation_id}>
                    <Td fontSize="xs" whiteSpace="nowrap">{formatCallTime(c.last_message_at || c.created_at, displayTimezone)}</Td>
                    <Td maxW="300px" whiteSpace="normal"><Text fontSize="sm" noOfLines={2}>{c.first_message}</Text></Td>
                    <Td>
                      <HStack wrap="wrap" spacing={1}>
                        {badges.map((b) => <Badge key={b.label} colorScheme={COLORS[b.kind]} textTransform="none">{b.label}</Badge>)}
                        {!badges.length && <Text fontSize="sm" color="gray.500">{zh ? '匿名访客' : 'Anonymous visitor'}</Text>}
                      </HStack>
                    </Td>
                    <Td>
                      {c.escalated_at ? <Badge colorScheme="red">{zh ? '触发工单' : 'Escalated'}</Badge> : !['requested', 'active'].includes(c.handoff_state) && <Text color="gray.400">—</Text>}
                      {['requested', 'active'].includes(c.handoff_state) && (
                        <Badge ml={c.escalated_at ? 1 : 0} colorScheme={handoffStateBadge(c.handoff_state, lang).color} textTransform="none">
                          {handoffStateBadge(c.handoff_state, lang).label}
                        </Badge>
                      )}
                    </Td>
                    <Td>{c.message_count}</Td>
                    <Td><Button as={RouterLink} size="xs" to={`${portalBasePath}/call-history/chat/${encodeURIComponent(c.conversation_id)}`} onClick={(event) => openConversation(event, c.conversation_id)}>{zh ? '查看' : 'Open'}</Button></Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        <Flex justify="space-between" mt={3}>
          <Text fontSize="sm" color="gray.600">{zh ? `共 ${total} 条` : `${total} total`}</Text>
          <HStack>
            <Button size="xs" isDisabled={page <= 1} onClick={() => changeList(filter, page - 1)}>{zh ? '上一页' : 'Previous'}</Button>
            <Button size="xs" isDisabled={page * PAGE_SIZE >= total} onClick={() => changeList(filter, page + 1)}>{zh ? '下一页' : 'Next'}</Button>
          </HStack>
        </Flex>
      </CardBody></Card>
    </Box>
  );
}

// backTo: where 返回 goes (the ecommerce 客服记录 Chatbox tab by default).
export function ChatRecordDetail({ portalBasePath, lang = 'zh', backTo, canOpenTicket = portalBasePath === '/app/ecommerce' }) {
  const zh = lang === 'zh';
  const { conversationId } = useParams();
  const location = useLocation();
  const ticket = validTicketNumber(new URLSearchParams(location.search).get('ticket'));
  const ticketBack = canOpenTicket ? ticketReturnPath(portalBasePath, ticket, location.state?.ticketReturnSearch) : null;
  const listParams = new URLSearchParams(location.state?.chatListSearch || 'source=chat');
  listParams.set('source', 'chat');
  const listSearch = new URLSearchParams();
  for (const key of ['source', 'chat_filter', 'chat_page']) if (listParams.has(key)) listSearch.set(key, listParams.get(key));
  const returnPath = backTo || ticketBack || `${portalBasePath}/call-history?${listSearch}`;
  const replyRef = useRef(null);
  const focusReply = () => {
    const input = replyRef.current?.querySelector('textarea');
    input?.scrollIntoView({ block: 'center', behavior: 'auto' });
    input?.focus({ preventScroll: true });
  };
  const [metadataOpen, setMetadataOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [displayTimezone] = useCallTimezone();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(false);
  const loadSequence = useRef(0);
  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    const next = await ChatWidgetAPI.chat(conversationId);
    if (sequence === loadSequence.current) { setDetail(next); setError(false); }
  }, [conversationId]);
  useEffect(() => {
    let live = true;
    setDetail(null); setError(false);
    load().catch((failure) => {
      if (live) {
        const status = failure?.response?.status;
        setError(status === 401 ? 'session' : status === 403 ? 'forbidden' : status === 404 ? 'missing' : 'unavailable');
      }
    });
    return () => { live = false; loadSequence.current += 1; };
  }, [load]);
  const refresh = useCallback(() => { load().catch(() => {}); }, [load]);
  const alertBadge = escalationAlertBadge(detail?.escalation?.alert, lang);
  const failureMessage = {
    session: zh ? '登录状态已失效，请重新登录后查看这条对话。' : 'Your session has expired. Sign in again to view this conversation.',
    forbidden: zh ? '你没有权限查看这条对话。请联系账号所有者。' : 'You do not have access to this conversation. Contact the account owner.',
    missing: zh ? '这条对话不可用，可能已删除。' : 'This conversation is unavailable or may have been deleted.',
    unavailable: zh ? '对话暂时无法加载，请稍后重试。' : 'This conversation could not be loaded right now. Try again later.',
  }[error];

  return (
    <Box p={{ base: 0, md: 2 }}>
      <Flex align="center" justify="space-between" gap={2} mb={3} wrap="wrap">
      <Button as={RouterLink} to={returnPath} state={{ chatListScroll: location.state?.chatListScroll }} size="sm" variant="ghost" leftIcon={<FiArrowLeft />} minH="44px">
        {ticketBack ? (zh ? '返回原工单' : 'Back to the ticket') : (zh ? '返回客服记录' : 'Back to service records')}
      </Button>
      {detail?.handoff_available && detail.handoff_enabled !== false && <Button display={{ base: 'inline-flex', md: 'none' }} minH="44px" minW="44px" size="sm" onClick={focusReply}>{zh ? '回复访客' : 'Reply to visitor'}</Button>}
      </Flex>
      {error && <Alert status="error"><AlertIcon />{failureMessage}</Alert>}
      {!detail && !error && <Spinner />}
      {detail && (
        <Box className="chat-detail-layout" display="grid" gridTemplateColumns={{ base: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 300px' }} gap={5} alignItems="start">
          <Box className="chat-detail-sidebar" gridColumn={{ lg: 2 }} gridRow={{ base: 1, lg: 1 }} as="details" open={metadataOpen} onToggle={(event) => setMetadataOpen(event.currentTarget.open)}>
            <Box as="summary" cursor="pointer" fontWeight="semibold" p={3}>{zh ? '联系人与关联请求' : 'Contact and linked request'}</Box>
          <Card mb={4}>
            <CardHeader pb={1}><Heading size="md">{zh ? '网页客服对话' : 'Website chat'}</Heading></CardHeader>
            <CardBody pt={1}>
              <Text fontSize="sm" color="gray.600">{formatCallTime(detail.created_at, displayTimezone)}</Text>
              <HStack mt={2} wrap="wrap" spacing={2}>
                {(detail.contact?.phones || []).map((p) => <Badge key={p} colorScheme="blue" textTransform="none">{zh ? '电话' : 'Phone'} {p}</Badge>)}
                {(detail.contact?.emails || []).map((e) => <Badge key={e} colorScheme="purple" textTransform="none">{zh ? '邮箱' : 'Email'} {e}</Badge>)}
                {(detail.contact?.order_numbers || []).map((o) => <Badge key={o} colorScheme="orange" textTransform="none">{zh ? '订单' : 'Order'} {o}</Badge>)}
              </HStack>
              {detail.escalation && (
                <Alert status="warning" mt={3} fontSize="sm" flexWrap="wrap">
                  <AlertIcon />
                  {zh ? '访客要求跟进：' : 'Visitor asked for follow-up: '}{detail.escalation.summary}
                  {detail.escalation.stored === false
                    ? (zh ? '（未建单，请直接联系访客）' : ' (Not stored — contact the visitor directly)')
                    : (zh ? '（已告知 48 小时内受理）' : ' (told it will be accepted within 48 hours)')}
                  {detail.escalation.stored === false ? null : String(detail.escalation.ticket_number || '').startsWith('CB')
                    ? <>
                        {zh ? `；已生成待回电请求 ${detail.escalation.ticket_number}，` : `; added as callback request ${detail.escalation.ticket_number}, `}
                        <Link as={RouterLink} to={`${portalBasePath}/callback-requests`} color="blue.600" textDecoration="underline">{zh ? '前往回电请求处理' : 'open callback requests'}</Link>
                      </>
                    : detail.escalation.ticket_number
                      ? <>{zh ? '；已进入工单中心，工单号 ' : '; in the ticket center as '}{canOpenTicket && ticketReturnPath(portalBasePath, detail.escalation.ticket_number) ? <Link as={RouterLink} to={ticketReturnPath(portalBasePath, detail.escalation.ticket_number, location.state?.ticketReturnSearch)} color="blue.600" textDecoration="underline">{detail.escalation.ticket_number}</Link> : detail.escalation.ticket_number}</>
                      : (zh ? '；未生成工单，请直接联系访客' : '; no ticket was created, contact the visitor directly')}
                  {alertBadge && <Badge ml={2} colorScheme={alertBadge.color} textTransform="none">{alertBadge.label}</Badge>}
                </Alert>
              )}
            </CardBody>
          </Card>
          </Box>
          <Box className="chat-detail-main" minW={0} gridColumn={{ lg: 1 }} gridRow={{ base: 2, lg: 1 }}>
          <CorrectionPanel key={`timeline-${detail.conversation_id}`} includeAllMessages recordRef={detail.conversation_id} source="chat" messages={detail.messages} lang={lang}
            turnBadges={(turn) => [guardBadge(turn.guard, lang)].filter(Boolean)}
            turnExtra={(turn) => <ChatAttachment conversationId={detail.conversation_id} message={turn} lang={lang} />} />
          <ChatHandoffPanel key={`composer-${detail.conversation_id}`} detail={detail} lang={lang} onChanged={refresh} showTranscript={false} composerRef={replyRef} />
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Front-desk accounts (e.g. Aiker's own site) have no 客服记录 page; this is their Chatbox list.
export default function ChatRecordsPage({ lang = 'zh' }) {
  return (
    <Box p={6}>
      <Heading size="lg" mb={2}>{lang === 'zh' ? '网页客服记录' : 'Website chat'}</Heading>
      <Text color="gray.600" fontSize="sm" mb={4}>
        {lang === 'zh' ? '官网“客服”入口里的对话。标“触发工单”的是访客要求跟进的，请按留下的联系方式联系。'
          : 'Conversations from the website Support button. Escalated ones asked for follow-up; contact them with the details they left.'}
      </Text>
      <ChatRecordList portalBasePath="/app" lang={lang} />
    </Box>
  );
}
